import { useEffect, useRef } from 'react';

import type { ApolloCache } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import {
  LocalDevicePresencePayload,
  WebSocketEntityType,
} from '@this+that/models';

import { GET_USER_MCP_CONNECTIONS } from '../../../services/mcpConnections.service';
import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

const LOCAL_DEVICE_TYPENAME = 'LocalDevice';

type PresenceFieldModifiers = Record<string, () => unknown>;

interface CachedMcpConnections {
  getUserMcpConnections: { connectionId: string }[];
}

/** Only the fields the payload actually carries: `$connect`/`$disconnect` send
 *  presence alone, so writing an absent `lastSeenAt` would blank a real one. */
export function buildPresenceFieldModifiers(
  item: LocalDevicePresencePayload,
): PresenceFieldModifiers {
  const modifiers: PresenceFieldModifiers = {
    isConnected: () => item.isConnected,
  };

  if (item.mcpConnectionId) {
    modifiers.mcpConnectionId = () => item.mcpConnectionId;
  }

  if (item.lastSeenAt) {
    modifiers.lastSeenAt = () => item.lastSeenAt;
  }

  return modifiers;
}

/** A never-loaded query counts as known: there is no stale list to correct, and the
 *  component that mounts next fetches it anyway. */
export function isMcpConnectionCached(
  cache: ApolloCache,
  connectionId: string,
): boolean {
  let cached: CachedMcpConnections | null = null;
  try {
    cached = cache.readQuery<CachedMcpConnections>({
      query: GET_USER_MCP_CONNECTIONS,
    });
  } catch {
    return true;
  }

  if (!cached) {
    return true;
  }

  return cached.getUserMcpConnections.some(
    (connection) => connection.connectionId === connectionId,
  );
}

/** True when this push is at least as recent as the last one applied for the device.
 *  A payload with no timestamp is always applied — an older backend still emits one. */
export function isFreshPresence(
  appliedAt: Map<string, number>,
  item: LocalDevicePresencePayload,
): boolean {
  if (typeof item.timestamp !== 'number') {
    return true;
  }

  const previous = appliedAt.get(item.deviceId);
  return previous === undefined || item.timestamp >= previous;
}

export function useLocalDevicePresenceSync() {
  const { subscribe } = useWebSocket();
  const client = useApolloClient();
  // $connect and $disconnect are independent invocations; their pushes can arrive
  // reordered, and a late "offline" would strand a live machine on the wrong badge.
  const appliedAtRef = useRef(new Map<string, number>());

  useEffect(() => {
    const appliedAt = appliedAtRef.current;

    return subscribe<LocalDevicePresencePayload>(
      WebSocketEntityType.LocalDevicePresence,
      WebSocketEventType.MODIFY,
      ({ item }) => {
        if (!item?.deviceId || !item.userId) {
          return;
        }

        if (!isFreshPresence(appliedAt, item)) {
          return;
        }

        if (typeof item.timestamp === 'number') {
          appliedAt.set(item.deviceId, item.timestamp);
        }

        const cacheId = client.cache.identify({
          __typename: LOCAL_DEVICE_TYPENAME,
          userId: item.userId,
          deviceId: item.deviceId,
        });

        // Absent id means the device list was never loaded in this session —
        // whatever fetch comes next reads the live value anyway.
        if (!cacheId) {
          return;
        }

        client.cache.modify({
          id: cacheId,
          fields: buildPresenceFieldModifiers(item),
        });

        // A first pairing creates the MCP connection inside registerEndpoint, so the
        // cached list predates it and the row renders without its name or Edit button.
        // The membership check is what keeps this from being a refetch per event: every
        // reconnect and every 10-minute heartbeat push finds the id already cached.
        if (
          item.mcpConnectionId &&
          !isMcpConnectionCached(client.cache, item.mcpConnectionId)
        ) {
          void client.refetchQueries({ include: [GET_USER_MCP_CONNECTIONS] });
        }
      },
    );
  }, [subscribe, client]);
}
